/**
 * Accept Invite Page - Trade Loop v2.0
 *
 * Handles organization invite links
 * Since accounts are auto-created when admin invites,
 * this page redirects users to login with instructions
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Building2,
  CheckCircle2,
  XCircle,
  Mail,
  ArrowRight,
  LogIn,
  RefreshCw,
} from "lucide-react";

interface InviteInfo {
  organizationName: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  expiresAt: string;
}

export default function AcceptInvitePage() {
  const { inviteCode: inviteCodeParam } = useParams<{ inviteCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();

  // Support both URL param (/accept-invite/:code) and query param (/accept-invite?token=xxx)
  const inviteCode = inviteCodeParam || searchParams.get('token') || '';

  const [isLoadingInvite, setIsLoadingInvite] = useState(true);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !isLoading && inviteCode) {
      // Redirect to login with return URL
      const returnUrl = `/accept-invite?token=${inviteCode}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
    }
  }, [isAuthenticated, isLoading, inviteCode, navigate]);

  // Verify invite code only when authenticated
  useEffect(() => {
    const verifyInvite = async () => {
      // Wait for auth to be ready
      if (isLoading) {
        console.log('[AcceptInvite] Waiting for auth to be ready...');
        return;
      }

      // If not authenticated, we'll redirect (handled by previous useEffect)
      if (!isAuthenticated) {
        console.log('[AcceptInvite] Not authenticated, will redirect');
        setIsLoadingInvite(false);
        return;
      }

      if (!inviteCode) {
        console.log('[AcceptInvite] No invite code provided');
        setError("Invalid invite link");
        setIsLoadingInvite(false);
        return;
      }

      console.log('[AcceptInvite] Verifying invite code:', inviteCode);
      try {
        // api.get() returns the data directly (not wrapped in response.data like axios)
        const inviteData = await api.get(`/api/workflows/organization/invite/verify/${inviteCode}`);
        console.log('[AcceptInvite] Invite data received:', inviteData);

        if (!inviteData || !inviteData.organizationName) {
          throw new Error('Invalid response format from server');
        }

        setInviteInfo(inviteData);
      } catch (err: any) {
        console.error('[AcceptInvite] Verification error:', err);
        if (err.response?.status === 404) {
          setError("This invitation link is invalid or has expired.");
        } else if (err.response?.status === 410) {
          setError("This invitation has already been used.");
        } else if (err.response?.status === 401) {
          setError("Authentication required. Please try logging in again.");
        } else {
          setError(`Unable to verify invitation: ${err.response?.data?.error || err.message || "Please try again."}`);
        }
      } finally {
        setIsLoadingInvite(false);
      }
    };

    verifyInvite();
  }, [inviteCode, isAuthenticated, isLoading]);

  // Don't auto-redirect - let user accept/decline the invitation first
  // useEffect(() => {
  //   if (isAuthenticated && inviteInfo && user?.email === inviteInfo.email) {
  //     navigate("/dashboard");
  //   }
  // }, [isAuthenticated, inviteInfo, user, navigate]);

  // Show loading while auth is initializing or while verifying invite
  if (isLoading || isLoadingInvite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              {!isAuthenticated ? "Redirecting to login..." : "Verifying invitation..."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-red-700">Invitation Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">{error}</p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Go to Login
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
              >
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If we get here, auth is loaded and invite verification is done
  // but we have no inviteInfo and no error - something unexpected happened
  if (!inviteInfo) {
    console.error('[AcceptInvite] Unexpected state: no inviteInfo and no error', {
      isAuthenticated,
      isLoading,
      isLoadingInvite,
      inviteCode,
      error
    });
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-amber-700">Something went wrong</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">Unable to load invitation details. Please try refreshing the page.</p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => window.location.reload()}
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
              >
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Role descriptions
  const getRoleDescription = (role: string) => {
    const descriptions: Record<string, {
      title: string;
      description: string;
      features: string[];
      restrictions: string[];
    }> = {
      member: {
        title: "Team Member",
        description: "Create referrals and send to organization admin for distribution",
        features: [
          "Create referrals and send to organization admin",
          "Message organization admin only",
          "View your own referrals you created",
          "View your own profile within the organization"
        ],
        restrictions: [
          "Cannot message other team members",
          "Cannot view analytics, partner network, or other members' referrals",
          "No public profile (admin only)",
          "Referrals are never sent directly to team members"
        ]
      },
    };

    return descriptions[role.toLowerCase()] || descriptions.member;
  };

  const roleInfo = getRoleDescription(inviteInfo.role);

  // Handle accept invitation
  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await api.post(`/api/workflows/organization/invite/accept/${inviteCode}`);
      // Redirect to organization page after successful acceptance
      navigate("/organization");
    } catch (error: any) {
      console.error('[AcceptInvite] Error accepting invitation:', error);
      // Show error to user
      alert(error.message || "Failed to accept invitation. Please try again.");
    } finally {
      setIsAccepting(false);
    }
  };

  // Handle decline invitation
  const handleDecline = async () => {
    if (!confirm("Are you sure you want to decline this invitation?")) {
      return;
    }

    setIsDeclining(true);
    try {
      await api.post(`/api/workflows/organization/invite/decline/${inviteCode}`);
      // Redirect to dashboard after declining
      navigate("/dashboard");
    } catch (error: any) {
      console.error('[AcceptInvite] Error declining invitation:', error);
      alert(error.message || "Failed to decline invitation. Please try again.");
    } finally {
      setIsDeclining(false);
    }
  };

  // Invite is valid - show acceptance screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center bg-gradient-to-r from-teal-50 to-emerald-50 rounded-t-lg pb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-teal-600" />
          </div>
          <CardTitle className="text-2xl text-gray-900 mb-2">
            You've Been Invited!
          </CardTitle>
          <p className="text-gray-600">
            Join <span className="font-semibold text-teal-700">{inviteInfo.organizationName}</span> on Trade Loop
          </p>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* Organization Info */}
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{inviteInfo.organizationName}</h3>
                  <p className="text-sm text-gray-600">Organization</p>
                </div>
              </div>
            </div>

            {/* Your Role */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Your Role: {roleInfo.title}</h3>
              <p className="text-sm text-gray-600 mb-4">{roleInfo.description}</p>

              {/* What you can do */}
              <div className="space-y-2 mb-4">
                <p className="text-sm font-medium text-gray-700">What you'll be able to do:</p>
                <ul className="space-y-1.5">
                  {roleInfo.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What you cannot do */}
              <div className="space-y-2 pt-3 border-t border-blue-200">
                <p className="text-sm font-medium text-gray-700">Access restrictions:</p>
                <ul className="space-y-1.5">
                  {roleInfo.restrictions.map((restriction, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>{restriction}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900 mb-1">Important Note</p>
                  <p className="text-sm text-amber-700">
                    {user?.email === inviteInfo.email
                      ? "By accepting, your existing personal account will be converted to an organization member account. Your personal data will be archived and organization features will become available."
                      : "A new account has been created for you. Check your email for login credentials."}
                  </p>
                </div>
              </div>
            </div>

            {/* Data Preservation Notice */}
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Your Personal Data</p>
                  <p className="text-sm text-blue-800 mb-3">
                    All of your prior Trade Loop data (messages, referrals, connections, etc.) will be <strong>hidden but preserved</strong> while you are a member of this organization.
                  </p>
                  <p className="text-sm text-blue-700">
                    You will not be able to access any of your personal data for as long as you remain an organization member. Should you leave the organization, all of your data will become available to you once again.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={handleAccept}
              disabled={isAccepting || isDeclining}
              className="flex-1 h-12 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold"
            >
              {isAccepting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Accept Invitation
                </>
              )}
            </Button>

            <Button
              onClick={handleDecline}
              disabled={isAccepting || isDeclining}
              variant="outline"
              className="flex-1 h-12 border-2 font-semibold"
            >
              {isDeclining ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Declining...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500">
            Expires on {new Date(inviteInfo.expiresAt).toLocaleDateString()}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
